/**
 * CustomerPortal — Customer-facing portal shell
 * Same layout as CustomerPage: identity strip, tab row with icons, filter drawer.
 * No TopNav, no consultant-only tabs. Sign out in portal nav.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useAuth, CustomerView } from '../../AuthProvider'
import GeoMap from '../../components/GeoMap/GeoMap'
import SolutionMatrix from '../../components/SolutionMatrix/SolutionMatrix'
import ContractView from '../../components/ContractView/ContractView'
import CustomerAvatar from '../../components/CustomerAvatar'
import DrillPanel from '../CustomerProfile/DrillPanel'
import ProfileFilterBar from '../CustomerProfile/ProfileFilterBar'
import { fetchCustomerProfile, fetchProfileFilters, fetchGeoDrilldown } from '../CustomerProfile/api'
import { tabBadgeCount } from '../CustomerProfile/findings'
import { executePinnedMetrics, deletePinnedMetric, getPortalSettings, setPortalTabs } from '../../api'
import { loadList } from '../../utils/loadState'
import ComplianceTab from '../CustomerProfile/ComplianceTab'
import HealthTab from '../CustomerProfile/HealthTab'
import MetricsTab from '../CustomerProfile/MetricsTab'
import OverviewTab from '../CustomerProfile/OverviewTab'
import XLR8Logo from '../../components/XLR8Logo'
import EmployeeLifeline from './Lifeline/EmployeeLifeline'
import EmployeePicker from './Lifeline/EmployeePicker'
import PortalDataMap from './PortalDataMap'
import '../CustomerProfile/CustomerProfile.css'
import '../CustomerProfile/DrillPanel.css'
import '../CustomerProfile/ProfileFilterBar.css'
import './CustomerPortal.css'
import { API_BASE as API_BASE_CONFIG } from '../../config'
import { unitMedianSalaryText } from '../CustomerProfile/money'
import { TAB_GUIDE, CONTACT_LINE } from './tabGuide'

// ─── Helpers ────────────────────────────────────────────────────────────────

const BAR_COLORS = ['var(--green-light)', 'var(--blue-light)', 'var(--purple-light)', 'var(--amber-light)', 'var(--red-light)']

function buildEffectiveFilters(activeFilters, filterDataResult) {
  if (!filterDataResult?.filters || !activeFilters) return activeFilters
  const effective = {}
  for (const [key, selectedValues] of Object.entries(activeFilters)) {
    if (!selectedValues || selectedValues.length === 0) continue
    const filterDef = filterDataResult.filters[key]
    if (!filterDef) { effective[key] = selectedValues; continue }
    const totalOptions = filterDef.options?.length || 0
    if (selectedValues.length >= totalOptions) continue
    effective[key] = selectedValues
  }
  return effective
}

// Every tab the portal can render — same keys/icons as CustomerPage consultant tabs.
// WHICH of them a customer sees is per customer (GET /api/portal/{id}/settings; keys in
// step with routers/portal.py PORTAL_TAB_KEYS). An admin previewing sees exactly the
// customer's set and can change it from the tab row.
const PORTAL_TABS = [
  { key: 'overview',     label: 'Overview',    icon: '🏢' },
  { key: 'organization', label: 'Organization', icon: '🗂️' },
  { key: 'metrics',      label: 'Metrics',      icon: '📊' },
  { key: 'compliance',   label: 'Compliance',   icon: '⚖️', badgeType: 'red' },
  { key: 'health',       label: 'Health',       icon: '🩺', badgeType: 'amber' },
  { key: 'system',       label: 'System',       icon: '⚙️' },
  { key: 'datamap',      label: 'Data map',     icon: '🕸️' },
  { key: 'contract',     label: 'Contract',     icon: '📑' },
]

// ─── Observation (inline drillable) ─────────────────────────────────────────

function Observation({ obs, customerId }) {
  const [expanded, setExpanded] = useState(false)
  const [drillData, setDrillData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [drillError, setDrillError] = useState(null)
  const [exporting, setExporting] = useState(false)

  const severityConfig = {
    risk:    { icon: '!',  cls: 'risk',    label: 'Critical' },
    insight: { icon: '⚠', cls: 'insight', label: 'Warning'  },
    note:    { icon: 'ⓘ', cls: 'note',    label: 'Info'     },
  }
  const cfg = severityConfig[obs.type] || severityConfig.note

  const handleClick = async () => {
    if (!obs.id) return
    if (expanded) { setExpanded(false); return }
    setExpanded(true)
    if (drillData) return
    setLoading(true); setDrillError(null)
    try {
      const API = API_BASE_CONFIG
      const token = localStorage.getItem('xlr8_token') || ''
      const res = await fetch(`${API}/api/observations/${customerId}/drilldown/${obs.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (res.ok) setDrillData(await res.json())
      else setDrillError(`HTTP ${res.status}`)
    } catch (e) { setDrillError(e.message || 'Network error') }
    setLoading(false)
  }

  const handleExport = async (e) => {
    e.stopPropagation()
    if (!drillData?.rows?.length) return
    setExporting(true)
    try {
      const cols = drillData.columns || Object.keys(drillData.rows[0])
      const csv = [cols.join(','), ...drillData.rows.map(r => cols.map(c => {
        const v = r[c] != null ? String(r[c]) : ''
        return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v
      }).join(','))].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url
      a.download = `${customerId}_observation_${obs.id?.slice(0, 8) || 'data'}.csv`
      a.click(); URL.revokeObjectURL(url)
    } catch (e) { console.error('Export failed:', e) }
    setExporting(false)
  }

  const currentStatus = obs._localStatus || obs.status
  const isDismissed = currentStatus && currentStatus !== 'open'

  return (
    <div className={`cp-obs ${obs.id ? 'clickable' : ''} ${isDismissed ? 'dismissed' : ''}`} onClick={handleClick}>
      <div className={`cp-obs-icon ${cfg.cls}`}>{cfg.icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="cp-obs-title">
          {obs.title || ''}
          {isDismissed && <span className={`cp-obs-status-badge ${currentStatus}`}>{currentStatus}</span>}
        </div>
        <div className="cp-obs-text">{obs.detail || ''}</div>
        {obs.source && <div className="cp-obs-meta">{obs.source}</div>}
        {expanded && (
          <div className="cp-obs-drill" onClick={e => e.stopPropagation()}>
            {loading && <div className="cp-obs-drill-loading">Loading data…</div>}
            {drillError && <div style={{ padding: '8px 0', fontSize: 12, color: 'var(--red)' }}>Failed to load: {drillError}</div>}
            {drillData?.rows?.length > 0 && (
              <>
                <div className="cp-obs-drill-table-wrap">
                  <table className="cp-obs-drill-table">
                    <thead><tr>{drillData.columns.map(c => <th key={c}>{c}</th>)}</tr></thead>
                    <tbody>
                      {drillData.rows.slice(0, 20).map((row, i) => (
                        <tr key={i}>{drillData.columns.map(c => <td key={c}>{row[c] != null ? String(row[c]) : '—'}</td>)}</tr>
                      ))}
                    </tbody>
                  </table>
                  {drillData.total_rows > 20 && <div className="cp-obs-drill-more">Showing 20 of {drillData.total_rows.toLocaleString()} rows</div>}
                </div>
                <div className="cp-obs-export-row">
                  <button className="cp-obs-export-btn" onClick={handleExport} disabled={exporting}>
                    {exporting ? 'Exporting…' : `↓ Export ${drillData.total_rows} rows`}
                  </button>
                </div>
              </>
            )}
            {drillData?.note && (!drillData.rows || drillData.rows.length === 0) && (
              <div className="cp-obs-drill-note">{drillData.note}</div>
            )}
          </div>
        )}
      </div>
      {obs.id && <div className="cp-obs-chevron">{expanded ? '▾' : '›'}</div>}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function CustomerPortal() {
  const { customerId: routeCustomerId } = useParams()
  const { profile: authProfile, isAdmin } = useAuth()
  // /portal (no id) is a customer's own portal: their one assigned customer.
  const customerId = routeCustomerId || authProfile?.customer_ids?.[0]
  // An admin previewing sees exactly what the customer sees (§0 item 208): the portal
  // renders inside CustomerView, so the server serves every call as that customer and
  // every isAdmin below is the customer's (false). Only the tab editor stays the admin's.
  if (isAdmin && customerId) {
    return <CustomerView key={customerId} customerId={customerId}><PortalPage customerId={customerId} /></CustomerView>
  }
  return <PortalPage customerId={customerId} />
}

function PortalPage({ customerId }) {
  const { signOut, profile: authProfile, isAdmin, customerView } = useAuth()
  // the tab editor: an admin choosing which tabs the customer sees, shown on the preview
  const canEditTabs = !!customerView

  // ── Employee Lifeline (queue item 219). ?lifeline=<reference> opens one person's
  // lifeline in place of the tab content; ?people=1 opens the picker (the "Employee
  // Lifeline" menu item). The reference is opaque: no employee number or company code is
  // ever in the URL (Scott, 2026-10-07). Offered only with view_employee_detail (the
  // server enforces it too).
  const [searchParams, setSearchParams] = useSearchParams()
  const canViewEmployee = (authProfile?.permissions || []).includes('view_employee_detail')
  const lifelineFor = canViewEmployee ? searchParams.get('lifeline') : null
  const pickerOpen = canViewEmployee && !lifelineFor && searchParams.get('people') === '1'
  // The retired ?employee=&company= form carried the number in the URL. It is not read;
  // it is scrubbed from the address bar and history on sight (replace, not push).
  useEffect(() => {
    if (searchParams.has('employee') || searchParams.has('company')) {
      const next = new URLSearchParams(searchParams)
      next.delete('employee')
      next.delete('company')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams])
  const openEmployee = useCallback((employeeRef) => {
    if (!employeeRef) return
    setSearchParams({ lifeline: String(employeeRef) })
    window.scrollTo?.(0, 0)
  }, [setSearchParams])
  const openPicker = useCallback(() => setSearchParams({ people: '1' }), [setSearchParams])
  const closeEmployee = useCallback(() => setSearchParams({}), [setSearchParams])
  const onOpenEmployee = canViewEmployee ? openEmployee : null

  // ── Which tabs this customer sees ──
  const [portalSettings, setPortalSettings] = useState(null)
  const [tabEditorOpen, setTabEditorOpen] = useState(false)
  const [tabDraft, setTabDraft] = useState([])
  const [tabSaving, setTabSaving] = useState(false)
  const [tabSaveError, setTabSaveError] = useState(null)
  const enabledTabs = portalSettings?.tabs || []
  const portalTabs = PORTAL_TABS.filter(t => enabledTabs.includes(t.key))
  // A tab that is off (or was just turned off) never renders: fall back to the first one on.
  const [activeTab, setActiveTab] = useState('overview')
  const [tabTip, setTabTip] = useState(null)   // {key, left, top}: the tab bar tooltip showing
  const currentTab = enabledTabs.includes(activeTab) ? activeTab : enabledTabs[0]

  // ── Core state ──
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)


  // ── Org state ──
  const [selectedDiv, setSelectedDiv] = useState(null)
  const [expandedDivs, setExpandedDivs] = useState(new Set())
  const [orgSearch, setOrgSearch] = useState('')
  const [orgView, setOrgView] = useState('location')
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [collapsedSections, setCollapsedSections] = useState(new Set())

  // ── System ──
  const [activeSystemId, setActiveSystemId] = useState(null)
  const [systems, setSystems] = useState([])
  const [profileRefreshing, setProfileRefreshing] = useState(false)

  // ── Pinned metrics ──
  const [pinnedMetrics, setPinnedMetrics] = useState([])
  // A failed pinned-metrics read is an error with a Retry on the Overview, never a
  // section that silently isn't there (item 201).
  const [pinnedError, setPinnedError] = useState(null)
  const loadPinned = useCallback(async () => {
    const { items, error } = await loadList(() => executePinnedMetrics(customerId))
    setPinnedMetrics(items || [])
    setPinnedError(error)
  }, [customerId])

  // ── Filters ──
  const [filterData, setFilterData] = useState(null)
  const [activeFilters, setActiveFilters] = useState({})
  const [filtersLoaded, setFiltersLoaded] = useState(false)
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const filterChangeRef = useRef(0)

  // ── Drill panel ──
  const [drillPanel, setDrillPanel] = useState(null)

  // ── Load ──
  useEffect(() => {
    let cancelled = false
    if (!customerId) return
    async function load() {
      try {
        setLoading(true)
        // A settings read failure fails the page loudly -- never silently "all tabs".
        const settings = await getPortalSettings(customerId)
        if (cancelled) return
        setPortalSettings(settings)

        let filtersResult = null
        try { filtersResult = await fetchProfileFilters(customerId) }
        catch (e) { console.warn('Filters not available:', e) }
        if (cancelled) return

        let defaults = {}
        if (filtersResult?.filters) {
          setFilterData(filtersResult)
          for (const [key, filterDef] of Object.entries(filtersResult.filters)) {
            if (filterDef.default) {
              const available = new Set(filterDef.options.map(o => o.value))
              defaults[key] = filterDef.default.filter(d => available.has(d))
            } else {
              defaults[key] = filterDef.options.map(o => o.value)
            }
          }
          setActiveFilters(defaults)
        }
        setFiltersLoaded(true)

        const effectiveDefaults = buildEffectiveFilters(defaults, filtersResult)
        const data = await fetchCustomerProfile(customerId, effectiveDefaults, null)
        if (cancelled) return

        if (data) {
          setProfile(data)
          const sysList = data.identity?.systems || []
          setSystems(sysList)
          if (sysList.length > 0) {
            const primary = sysList.find(s => s.is_primary) || sysList[0]
            if (primary) setActiveSystemId(primary.id)
          }
          if (data.org_tree?.length > 0) {
            setSelectedDiv(data.org_tree[0].name)
            setExpandedDivs(new Set([data.org_tree[0].name]))
          }
          loadPinned()
        }
      } catch (err) {
        if (!cancelled) setError(err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [customerId])

  const handleFiltersChange = useCallback(async (newFilters) => {
    setActiveFilters(newFilters)
    const changeId = ++filterChangeRef.current
    setProfileRefreshing(true)
    try {
      const effective = buildEffectiveFilters(newFilters, filterData)
      const data = await fetchCustomerProfile(customerId, effective, activeSystemId)
      if (changeId === filterChangeRef.current) {
        if (data) setProfile(data)
        if (data.org_tree?.length > 0 && !data.org_tree.find(d => d.name === selectedDiv)) {
          setSelectedDiv(data.org_tree[0].name)
          setExpandedDivs(new Set([data.org_tree[0].name]))
        }
      }
    } catch (err) { console.error('Profile refresh failed:', err) }
    finally { if (changeId === filterChangeRef.current) setProfileRefreshing(false) }
  }, [customerId, selectedDiv, filterData, activeSystemId])

  const toggleDiv = useCallback((divName) => {
    setExpandedDivs(prev => {
      const next = new Set(prev)
      next.has(divName) ? next.delete(divName) : next.add(divName)
      return next
    })
    setSelectedDiv(divName)
  }, [])

  const toggleSection = (name) => {
    setCollapsedSections(prev => {
      const next = new Set(prev)
      next.has(name) ? next.delete(name) : next.add(name)
      return next
    })
  }

  // ── Loading / error ──
  if (loading) {
    return (
      <div className="cportal-root">
        <div className="cportal-loading">
          <div className="dm-spinner" style={{ width: 28, height: 28, marginRight: 12 }} />
          Loading…
        </div>
      </div>
    )
  }
  if (error || !profile) {
    return (
      <div className="cportal-root">
        <div className="cportal-error">Failed to load: {error || 'No data'}</div>
      </div>
    )
  }

  const { identity, kpis, org_tree, work_locations, geography, geo_type, composition,
          observations, data_freshness, system_configuration, complexity } = profile
  const companyName = identity?.company_name || customerId

  const hasActiveFilters = filterData ? Object.keys(filterData.filters || {}).some(key => {
    const sel = activeFilters[key] || []
    const opts = filterData.filters[key]?.options || []
    const defs = filterData.filters[key]?.default || []
    if (defs.length > 0) { const ds = new Set(defs), ss = new Set(sel); if (ds.size !== ss.size) return true; for (const d of ds) if (!ss.has(d)) return true; return false }
    return sel.length > 0 && sel.length < opts.length
  }) : false

  const selectedDivData = org_tree?.find(d => d.name === selectedDiv)

  return (
    <div className="cportal-root">

      {/* ── PORTAL NAV — branded, minimal ── */}
      <div className="cportal-nav">
        <div className="cportal-nav-left">
          <div className="cportal-nav-brand">
            <img src="/wordmark-light.svg" alt="Sempirical" className="cportal-nav-wordmark" />
          </div>
          <div className="cportal-nav-divider" />
          <div className="cportal-nav-cust">
            <CustomerAvatar customerId={customerId} companyName={companyName} website={identity?.website} size="xs" />
            <span className="cportal-nav-cust-name">{companyName}</span>
          </div>
        </div>
        <div className="cportal-nav-right">
          <div className="cportal-nav-meta">
            {kpis.total_headcount ? `${kpis.total_headcount.toLocaleString()} employees` : ''}
            {identity?.hcm_product ? ` · ${identity.hcm_product}` : ''}
          </div>
          <button className="cportal-signout-btn" onClick={signOut}>Sign Out</button>
        </div>
      </div>

      {/* ── TAB ROW — icons matching CustomerPage ── */}
      <div className="cportal-tab-row">
        {portalTabs.map(tab => {
          // a findings tab's badge is its own Open count over the findings this viewer is sent
          const badge = tabBadgeCount(observations, tab.key)
          const tip = TAB_GUIDE[tab.key]
          // The tab's description is its tooltip, on hover and on keyboard focus. The row
          // scrolls sideways (overflow), which would clip a tooltip inside it, so the visible
          // tooltip floats below the row; aria-describedby gives screen readers the same text.
          const show = e => { if (!tip) return; const r = e.currentTarget.getBoundingClientRect(); setTabTip({ key: tab.key, left: r.left, top: r.bottom + 6 }) }
          return (
            <div
              key={tab.key}
              className={`cportal-tab ${currentTab === tab.key && !lifelineFor && !pickerOpen ? 'active' : ''}`}
              onClick={() => { setActiveTab(tab.key); if (lifelineFor || pickerOpen) closeEmployee() }}
              role="tab"
              tabIndex={0}
              aria-selected={currentTab === tab.key}
              aria-describedby={tip ? `cportal-tab-desc-${tab.key}` : undefined}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveTab(tab.key); if (lifelineFor || pickerOpen) closeEmployee() } }}
              onMouseEnter={show} onFocus={show}
              onMouseLeave={() => setTabTip(null)} onBlur={() => setTabTip(null)}
            >
              {tab.icon && <span className="cportal-tab-icon">{tab.icon}</span>}
              {tab.label}
              {badge > 0 && <span className={`cportal-tab-badge ${tab.badgeType}`}>{badge}</span>}
              {tip && <span id={`cportal-tab-desc-${tab.key}`} hidden>{tip}</span>}
            </div>
          )
        })}
        {canViewEmployee && (
          <button
            type="button"
            className={`cportal-tab cportal-tab--menu ${lifelineFor || pickerOpen ? 'active' : ''}`}
            aria-pressed={!!(lifelineFor || pickerOpen)}
            onClick={openPicker}
          >
            <span className="cportal-tab-icon" aria-hidden="true">🧭</span>
            Employee Lifeline
          </button>
        )}
        {canEditTabs && (
          <button
            className="cportal-tab-edit-btn"
            onClick={() => { setTabDraft(enabledTabs); setTabSaveError(null); setTabEditorOpen(v => !v) }}
            title="Choose which tabs this customer sees"
          >
            {tabEditorOpen ? 'Close' : 'Customer tabs…'}
          </button>
        )}
      </div>
      {tabTip && TAB_GUIDE[tabTip.key] && (
        <div role="tooltip" className="cportal-tab-tip" style={{ left: tabTip.left, top: tabTip.top }}>
          {TAB_GUIDE[tabTip.key]}
        </div>
      )}

      {/* ── ADMIN: which tabs this customer sees ── */}
      {canEditTabs && tabEditorOpen && (
        <div className="cportal-tab-editor">
          <span className="cportal-tab-editor-label">
            {companyName} sees{portalSettings?.configured ? '' : ' (platform default)'}:
          </span>
          {PORTAL_TABS.map(t => (
            <label key={t.key} className="cportal-tab-editor-opt">
              <input
                type="checkbox"
                checked={tabDraft.includes(t.key)}
                onChange={e => setTabDraft(d => e.target.checked ? [...d, t.key] : d.filter(k => k !== t.key))}
              />
              {t.label}
            </label>
          ))}
          <button
            className="cportal-tab-editor-save"
            disabled={tabSaving || tabDraft.length === 0}
            onClick={async () => {
              setTabSaving(true); setTabSaveError(null)
              try {
                const saved = await setPortalTabs(customerId, tabDraft)
                setPortalSettings(saved)
                setTabEditorOpen(false)
              } catch (e) { setTabSaveError(e.message) }
              finally { setTabSaving(false) }
            }}
          >
            {tabSaving ? 'Saving…' : 'Save'}
          </button>
          {tabDraft.length === 0 && <span className="cportal-tab-editor-err">Pick at least one tab</span>}
          {tabSaveError && <span className="cportal-tab-editor-err">Not saved: {tabSaveError}</span>}
        </div>
      )}

      {/* ── FILTER DRAWER ── */}
      {filtersLoaded && filterData && (
        <>
          <button
            className={`cportal-filter-pull-tab ${filterDrawerOpen ? 'open' : ''}`}
            onClick={() => setFilterDrawerOpen(v => !v)}
            title="Filters"
          >
            <span className="cportal-filter-pull-icon">⏣</span>
            <span className="cportal-filter-pull-label">Filters</span>
            {hasActiveFilters && <span className="cportal-filter-pull-dot" />}
          </button>

          <div className={`cportal-filter-drawer ${filterDrawerOpen ? 'open' : ''}`}>
            <div className="cportal-filter-drawer-head">
              <span className="cportal-filter-drawer-title">⏣ Filters</span>
              <button className="cportal-filter-drawer-close" onClick={() => setFilterDrawerOpen(false)}>✕</button>
            </div>
            <div className="cportal-filter-drawer-body">
              <ProfileFilterBar
                customerId={customerId}
                filterData={filterData}
                activeFilters={activeFilters}
                onFiltersChange={handleFiltersChange}
                inlineMode={true}
              />
            </div>
          </div>
          {filterDrawerOpen && <div className="cportal-filter-drawer-backdrop" onClick={() => setFilterDrawerOpen(false)} />}
        </>
      )}

      {/* ── BODY ── */}
      <div className="cportal-body">
        <main className="cportal-main">
          <div className="cportal-content">
            <div className="cportal-content-inner">

              {/* ── IDENTITY STRIP ── */}
              <div className="cportal-identity-strip">
                <CustomerAvatar
                  customerId={customerId}
                  companyName={identity?.company_name}
                  website={identity?.website}
                  size="xl"
                />
                <div className="cportal-identity-info">
                  <div className="cportal-identity-name">{companyName}</div>
                  <div className="cportal-identity-meta">
                    {[identity?.industry, identity?.hcm_product || identity?.hcm_vendor].filter(Boolean).join(' · ')}
                  </div>
                </div>
              </div>

              {lifelineFor && (
                <EmployeeLifeline
                  key={lifelineFor}
                  customerId={customerId}
                  employeeRef={lifelineFor}
                  onClose={closeEmployee}
                  onPickAnother={openPicker}
                />
              )}
              {pickerOpen && (
                <EmployeePicker customerId={customerId} onOpen={openEmployee} onClose={closeEmployee} />
              )}

              {/* ── OVERVIEW ── */}
              {!lifelineFor && !pickerOpen && currentTab === 'overview' && (
                <>
                  <OverviewTab
                    customerId={customerId}
                    kpis={kpis}
                    observations={observations}
                    data_freshness={data_freshness}
                    pinnedMetrics={pinnedMetrics}
                    pinnedError={pinnedError}
                    onRetryPinned={loadPinned}
                    setPinnedMetrics={setPinnedMetrics}
                    onTabSwitch={(tab) => setActiveTab(tab)}
                    deletePinnedMetric={deletePinnedMetric}
                    composition={composition}
                    geography={geography}
                    geo_type={geo_type}
                    customerMode={true}
                    userName={authProfile?.display_name || ''}
                    companyName={companyName}
                    tabs={portalTabs}
                  />
                  {/* ── Product Utilization ── */}
                  {system_configuration?.product_utilization?.length > 0 && (() => {
                    const total = system_configuration.product_utilization.length
                    const inUse = system_configuration.product_utilization.filter(p => p.in_use).length
                    const unused = total - inUse
                    return (
                      <div className="cp-section">
                        <div className="cp-section-header">
                          <div>
                            <div className="cp-section-title">UKG Product Utilization</div>
                            <div className="cp-section-subtitle">
                              {inUse} of {total} products active
                              {unused > 0 && <span className="cp-produtil-alert"> · {unused} paying but unused</span>}
                            </div>
                          </div>
                        </div>
                        <div className="cp-produtil-table">
                          <div className="cp-produtil-header">
                            <span>Product Key</span>
                            <span>Status</span>
                          </div>
                          {system_configuration.product_utilization.map((p, i) => (
                            <div key={i} className="cp-produtil-row">
                              <span className="cp-produtil-key">{p.key}</span>
                              <span className={`cp-produtil-badge ${p.in_use ? 'in-use' : 'not-in-use'}`}>
                                {p.in_use ? '✓ Active' : '✗ Inactive'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })()}
                </>
              )}

              {/* ── ORGANIZATION ── */}
              {!lifelineFor && !pickerOpen && currentTab === 'organization' && (
                <>
                  {geography && geography.length > 0 && (
                    <div className="cp-section">
                      <div className="cp-section-header cp-section-toggle" onClick={() => toggleSection('geo')}>
                        <div>
                          <div className="cp-section-title">
                            <span className="cp-section-chevron">{collapsedSections.has('geo') ? '▸' : '▾'}</span>
                            Geographic Distribution
                          </div>
                          <div className="cp-section-subtitle">{geography.length} {geography.every(g => /^[A-Z]{2}$/.test(g.state)) ? 'states' : 'locations'}</div>
                        </div>
                      </div>
                      {!collapsedSections.has('geo') && (
                        <GeoMap geography={geography} geoType={profile?.geo_type} customerId={customerId} fetchDrilldown={fetchGeoDrilldown} workLocations={work_locations} />
                      )}
                    </div>
                  )}

                  {org_tree && org_tree.length > 0 && (
                    <div className="cp-section">
                      <div className="cp-section-header cp-section-toggle" onClick={() => toggleSection('org')}>
                        <div>
                          <div className="cp-section-title">
                            <span className="cp-section-chevron">{collapsedSections.has('org') ? '▸' : '▾'}</span>
                            Organizational Structure
                          </div>
                          <div className="cp-section-subtitle">Click any level to see headcount and comp</div>
                        </div>
                        {!collapsedSections.has('org') && (
                          <div className="cp-section-tabs">
                            <button className={`cp-tab ${orgView === 'division' ? 'active' : ''}`} onClick={e => { e.stopPropagation(); setOrgView('division') }}>Division → Dept</button>
                            <button className={`cp-tab ${orgView === 'location' ? 'active' : ''}`} onClick={e => { e.stopPropagation(); setOrgView('location') }}>Location</button>
                          </div>
                        )}
                      </div>

                      {!collapsedSections.has('org') && orgView === 'division' && (
                        <div className="cp-org-grid">
                          <div className="cp-org-tree">
                            <div className="cp-org-tree-header">
                              <span>Org Tree</span>
                              <span style={{ fontSize: 10, fontWeight: 400 }}>{org_tree.length} divisions</span>
                            </div>
                            <div className="cp-org-search">
                              <input type="text" placeholder="Search divisions…" value={orgSearch} onChange={e => setOrgSearch(e.target.value)} />
                            </div>
                            <div className="cp-org-list">
                              {org_tree.filter(d => !orgSearch || d.name.toLowerCase().includes(orgSearch.toLowerCase())).map(div => (
                                <div key={div.name}>
                                  <div className={`cp-org-node l0 ${selectedDiv === div.name ? 'active' : ''}`} onClick={() => toggleDiv(div.name)}>
                                    <span className="cp-org-expand">{expandedDivs.has(div.name) && div.departments?.length > 0 ? '▾' : '▸'}</span>
                                    <span className="cp-org-label">{div.name}</span>
                                    <span className="cp-org-count">{div.headcount}</span>
                                  </div>
                                  {expandedDivs.has(div.name) && div.departments?.map(dept => (
                                    <div key={dept.name} className="cp-org-node l1">
                                      <span className="cp-org-expand" />
                                      <span className="cp-org-label">{dept.name}</span>
                                      <span className="cp-org-count">{dept.headcount}</span>
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="cp-org-detail">
                            {selectedDivData ? (
                              <>
                                <div className="cp-org-detail-header">
                                  <div className="cp-org-detail-title">{selectedDivData.name}</div>
                                  <div className="cp-org-detail-path">{companyName} → {selectedDivData.name} · {(selectedDivData.headcount / kpis.total_headcount * 100).toFixed(0)}% of company</div>
                                </div>
                                <div className="cp-org-detail-kpis">
                                  <div className="cp-org-kpi"><div className="cp-org-kpi-value">{selectedDivData.headcount}</div><div className="cp-org-kpi-label">Headcount</div></div>
                                  <div className="cp-org-kpi"><div className="cp-org-kpi-value" style={{ color: 'var(--blue)' }}>{unitMedianSalaryText(selectedDivData)}</div><div className="cp-org-kpi-label">Median Salary</div></div>
                                  <div className="cp-org-kpi"><div className="cp-org-kpi-value">{selectedDivData.state_count || '—'}</div><div className="cp-org-kpi-label">States</div></div>
                                  <div className="cp-org-kpi"><div className="cp-org-kpi-value">{selectedDivData.departments?.length || 0}</div><div className="cp-org-kpi-label">Departments</div></div>
                                </div>
                                <div className="cp-org-detail-body">
                                  {selectedDivData.departments?.length > 0 && (
                                    <>
                                      <div className="cp-obs-cat">Departments in {selectedDivData.name}</div>
                                      {selectedDivData.departments.map((dept, i) => (
                                        <div key={dept.name} className="cp-bar" style={{ padding: '6px 0' }}>
                                          <span className="cp-bar-label">{dept.name}</span>
                                          <div className="cp-bar-track"><div className="cp-bar-fill" style={{ width: `${dept.headcount / selectedDivData.headcount * 100}%`, background: BAR_COLORS[i % BAR_COLORS.length] }} /></div>
                                          <span className="cp-bar-value">{dept.headcount} <span className="cp-bar-pct">{(dept.headcount / selectedDivData.headcount * 100).toFixed(0)}%</span></span>
                                        </div>
                                      ))}
                                    </>
                                  )}
                                </div>
                              </>
                            ) : (
                              <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)', fontSize: 13 }}>Select a division</div>
                            )}
                          </div>
                        </div>
                      )}

                      {!collapsedSections.has('org') && orgView === 'location' && work_locations?.length > 0 && (() => {
                        const filteredLocs = work_locations.filter(wl => !orgSearch || wl.description?.toLowerCase().includes(orgSearch.toLowerCase()) || wl.code?.toLowerCase().includes(orgSearch.toLowerCase()))
                        const selCode = selectedLocation || work_locations[0]?.code
                        const selLoc = work_locations.find(wl => wl.code === selCode) || work_locations[0]
                        const maxLocCount = work_locations[0]?.employee_count || 1
                        return (
                          <div className="cp-org-grid">
                            <div className="cp-org-tree">
                              <div className="cp-org-tree-header"><span>Work Locations</span><span style={{ fontSize: 10, fontWeight: 400 }}>{work_locations.length} locations</span></div>
                              <div className="cp-org-search"><input type="text" placeholder="Search locations…" value={orgSearch} onChange={e => setOrgSearch(e.target.value)} /></div>
                              <div className="cp-org-list">
                                {filteredLocs.map(wl => (
                                  <div key={wl.code} className={`cp-org-node l0 ${selCode === wl.code ? 'active' : ''}`} onClick={() => setSelectedLocation(wl.code)}>
                                    <span className="cp-org-expand" />
                                    <span className="cp-org-label"><span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', marginRight: 6 }}>{wl.code}</span>{wl.description || ''}</span>
                                    <span className="cp-org-count">{wl.employee_count}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="cp-org-detail">
                              {selLoc && (
                                <>
                                  <div className="cp-org-detail-header">
                                    <div className="cp-org-detail-title">{selLoc.description || selLoc.code}</div>
                                    <div className="cp-org-detail-path">Code: {selLoc.code}{selLoc.city ? ` · ${selLoc.city}` : ''}{selLoc.state ? `, ${selLoc.state}` : ''} · {(selLoc.employee_count / kpis.total_headcount * 100).toFixed(1)}% of workforce</div>
                                  </div>
                                  <div className="cp-org-detail-kpis">
                                    <div className="cp-org-kpi"><div className="cp-org-kpi-value">{selLoc.employee_count.toLocaleString()}</div><div className="cp-org-kpi-label">Headcount</div></div>
                                    <div className="cp-org-kpi"><div className="cp-org-kpi-value" style={{ color: 'var(--blue)' }}>{(selLoc.employee_count / kpis.total_headcount * 100).toFixed(1)}%</div><div className="cp-org-kpi-label">% of Total</div></div>
                                    <div className="cp-org-kpi"><div className="cp-org-kpi-value">#{work_locations.indexOf(selLoc) + 1}</div><div className="cp-org-kpi-label">Rank</div></div>
                                  </div>
                                  <div className="cp-org-detail-body">
                                    <div className="cp-obs-cat">Top Locations</div>
                                    {work_locations.slice(0, 20).map((wl, i) => (
                                      <div key={wl.code} className="cp-bar" style={{ padding: '5px 0' }}>
                                        <span className="cp-bar-label" style={{ fontWeight: wl.code === selLoc.code ? 700 : 400 }}><span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, opacity: 0.6, marginRight: 4 }}>{wl.code}</span>{wl.description || wl.code}</span>
                                        <div className="cp-bar-track"><div className="cp-bar-fill" style={{ width: `${wl.employee_count / maxLocCount * 100}%`, background: wl.code === selLoc.code ? 'var(--blue)' : BAR_COLORS[i % BAR_COLORS.length] }} /></div>
                                        <span className="cp-bar-value">{wl.employee_count.toLocaleString()}</span>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        )
                      })()}
                    </div>
                  )}
                </>
              )}

              {/* ── METRICS ── */}
              {!lifelineFor && !pickerOpen && currentTab === 'metrics' && (
                <MetricsTab customerId={customerId} activeFilters={activeFilters} filterData={filterData} onOpenEmployee={onOpenEmployee} />
              )}

              {/* ── COMPLIANCE ── */}
              {!lifelineFor && !pickerOpen && currentTab === 'compliance' && (
                <ComplianceTab customerId={customerId} observations={observations} onOpenEmployee={onOpenEmployee} />
              )}

              {/* ── HEALTH ── */}
              {!lifelineFor && !pickerOpen && currentTab === 'health' && (
                <HealthTab customerId={customerId} observations={observations} onOpenEmployee={onOpenEmployee} />
              )}

              {/* ── CONTRACT — customer-scoped endpoints, refused server-side unless enabled ── */}
              {!lifelineFor && !pickerOpen && currentTab === 'datamap' && enabledTabs.includes('datamap') && (
                <PortalDataMap customerId={customerId} />
              )}

              {!lifelineFor && !pickerOpen && currentTab === 'contract' && enabledTabs.includes('contract') && (
                <ContractView customerId={customerId} customerName={identity?.company_name} embedded portal />
              )}

              {/* ── SYSTEM ── */}
              {!lifelineFor && !pickerOpen && currentTab === 'system' && (
                <>
                  {system_configuration && (
                    system_configuration.companies?.length > 0 ||
                    system_configuration.org_levels?.length > 0 ||
                    system_configuration.earning_groups?.length > 0 ||
                    system_configuration.deduction_groups?.length > 0
                  ) && (
                    <div className="cp-section">
                      <div className="cp-section-header">
                        <div>
                          <div className="cp-section-title">System Configuration</div>
                          <div className="cp-section-subtitle">Companies, org levels, earning & deduction groups</div>
                        </div>
                      </div>
                      <div className="cp-sysconfig-grid">
                        {system_configuration.companies?.length > 0 && (
                          <div className="cp-sysconfig-card">
                            <div className="cp-sysconfig-card-header">
                              <span className="cp-sysconfig-card-icon" style={{ background: 'var(--blue-light)', color: 'var(--blue)' }}>🏢</span>
                              <div>
                                <div className="cp-sysconfig-card-title">Companies</div>
                                <div className="cp-sysconfig-card-count">{system_configuration.companies.length} companies</div>
                              </div>
                            </div>
                            <div className="cp-sysconfig-list">
                              {system_configuration.companies.map((c, i) => (
                                <div key={i} className="cp-sysconfig-row">
                                  <span className="cp-sysconfig-code">{c.code}</span>
                                  <span className="cp-sysconfig-name">{c.name}</span>
                                  {c.employee_count != null && <span className="cp-sysconfig-count">{c.employee_count.toLocaleString()}</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {system_configuration.org_levels?.length > 0 && (
                          <div className="cp-sysconfig-card">
                            <div className="cp-sysconfig-card-header">
                              <span className="cp-sysconfig-card-icon" style={{ background: 'var(--green-light)', color: 'var(--green-dark)' }}>🏗️</span>
                              <div>
                                <div className="cp-sysconfig-card-title">Org Levels</div>
                                <div className="cp-sysconfig-card-count">{system_configuration.org_levels.length} levels configured</div>
                              </div>
                            </div>
                            <div className="cp-sysconfig-list">
                              {system_configuration.org_levels.map((ol, i) => (
                                <div key={i} className="cp-sysconfig-org-level">
                                  <div className="cp-sysconfig-row">
                                    <span className="cp-sysconfig-level-num">L{ol.level}</span>
                                    <span className="cp-sysconfig-name cp-sysconfig-name-bold">{ol.label}</span>
                                    <span className="cp-sysconfig-count">{ol.active_codes} active</span>
                                  </div>
                                  {ol.sample_values?.length > 0 && (
                                    <div className="cp-sysconfig-samples">
                                      {ol.sample_values.slice(0, 5).map((sv, j) => (
                                        <span key={j} className="cp-sysconfig-sample">{sv.description || sv.code}</span>
                                      ))}
                                      {ol.active_codes > 5 && <span className="cp-sysconfig-sample cp-sysconfig-more">+{ol.active_codes - 5} more</span>}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      {(system_configuration.earning_groups?.length > 0 || system_configuration.deduction_groups?.length > 0) && (
                        <div className="cp-sysconfig-groups-row">
                          {system_configuration.earning_groups?.length > 0 && (
                            <div className="cp-sysconfig-group-block">
                              <span className="cp-sysconfig-group-label">Earning Groups</span>
                              <div className="cp-sysconfig-pills">
                                {system_configuration.earning_groups.map((eg, i) => (
                                  <span key={i} className="cp-sysconfig-pill cp-sysconfig-pill-amber">{eg.code} <span className="cp-sysconfig-pill-count">{eg.employee_count.toLocaleString()}</span></span>
                                ))}
                              </div>
                            </div>
                          )}
                          {system_configuration.deduction_groups?.length > 0 && (
                            <div className="cp-sysconfig-group-block">
                              <span className="cp-sysconfig-group-label">Deduction Groups</span>
                              <div className="cp-sysconfig-pills">
                                {system_configuration.deduction_groups.map((dg, i) => (
                                  <span key={i} className="cp-sysconfig-pill cp-sysconfig-pill-purple">{dg.code} <span className="cp-sysconfig-pill-count">{dg.employee_count.toLocaleString()}</span></span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="cp-section">
                    <div className="cp-section-header">
                      <div>
                        <div className="cp-section-title">Solution Matrix</div>
                        <div className="cp-section-subtitle">{isAdmin ? 'Registered vs available product keys' : 'What your system is contracted for, and what it is not'}</div>
                      </div>
                    </div>
                    <SolutionMatrix customerId={customerId} vendorLabel={identity?.hcm_product || identity?.hcm_vendor || ''} portal />
                  </div>

                  {complexity && (
                    <div className="cp-section">
                      <div className="cp-section-header">
                        <div>
                          <div className="cp-section-title">System Complexity</div>
                          <div className="cp-section-subtitle">What makes this setup complex to operate and keep compliant.</div>
                        </div>
                      </div>
                      <div className="cp-complexity">
                        {Object.entries(complexity).map(([key, cx]) => (
                          <div key={key} className="cp-cx-card">
                            <div className="cp-cx-label">{key.replace(/_/g, ' ')}</div>
                            <div className="cp-cx-value">{cx.label}</div>
                            <div className="cp-cx-detail">{cx.detail}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Footer */}
              <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--gray-400)', marginTop: 24, paddingBottom: 16 }}>
                <div style={{ marginBottom: 4 }}>{CONTACT_LINE}</div>
                Powered by Sempirical{isAdmin && profile.processing_ms != null ? ` · ${data_freshness.table_count} tables · ${profile.processing_ms}ms` : ''}
              </div>

            </div>{/* cportal-content-inner */}
          </div>
        </main>
      </div>

      {drillPanel && (
        <DrillPanel
          customerId={customerId}
          queryId={drillPanel.queryId}
          filters={drillPanel.filters}
          title={drillPanel.title}
          subtitle={drillPanel.subtitle}
          onClose={() => setDrillPanel(null)}
        />
      )}
    </div>
  )
}
